import { useEffect, useRef, useState } from 'react';
import type { OwnedAccount } from '@hotjar/account';
import type {
  AnalyticsProduct,
  FeedbackProduct,
  ProductCatalog,
} from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import {
  getAnalyticsProductBySku,
  getBundledPlanId,
  getFeedbackProductBySku,
  getProductSkusByPlanId,
} from '@hotjar/webapp/domains/common/product/utils';
import type { Site } from '@hotjar/site';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/plan';
import type { PriceInfo } from '@hotjar/webapp/domains/revenue/pricing/types';
import {
  CostContainer,
  DetailsContainer,
  SiteName,
  StyledBox,
} from '@hotjar/webapp/features/Billing/SiteBox/Sitebox.styles';
import { isPlanDowngradePeriod } from '@hotjar/site';
import { SitePriceMultiSku } from '../SitePrice/SitePriceMultiSku';
import { MultiSkuAllowance } from './components/MultiSkuAllowance';
import { MultiSkuCollectionRates } from './components/MultiSkuCollectionRates';

interface Props {
  site: Site;
  account: OwnedAccount;
  hasDivider: boolean;
  productCatalog: ProductCatalog;
  priceInfo: PriceInfo;
  onPlanChange: (
    organizationId: number,
    siteId: number,
    plan: Pick<Plan, 'identifier'>,
  ) => Promise<void>;
}

export const MultiSkuSiteBox = ({
  account,
  site,
  hasDivider,
  productCatalog,
  priceInfo,
  onPlanChange,
}: Props): JSX.Element => {
  const [changedSite, setChangedSite] = useState<Site>(site);
  const originalIdentifier = useRef<string>(site.planId);

  const [isChanged, setIsChanged] = useState<boolean>(false);

  const [activeAnalyticsProduct, setActiveAnalyticsProduct] = useState<
    AnalyticsProduct | undefined
  >();
  const [activeFeedbackProduct, setActiveFeedbackProduct] = useState<FeedbackProduct | undefined>();

  const setActiveProducts = (selectedProduct: FeedbackProduct | AnalyticsProduct): string => {
    const [analyticsProdSku, feedbackProdSku] = getProductSkusByPlanId(changedSite.planId);
    if (selectedProduct.type === ProductType.Analytics) {
      setActiveAnalyticsProduct(selectedProduct as AnalyticsProduct);
    } else {
      setActiveFeedbackProduct(selectedProduct as FeedbackProduct);
    }

    const concatProdId = getBundledPlanId({
      analyticsPlanId:
        selectedProduct.type === ProductType.Analytics ? selectedProduct.sku : analyticsProdSku,
      feedbackPlanId:
        selectedProduct.type === ProductType.Feedback ? selectedProduct.sku : feedbackProdSku,
    });

    setChangedSite({ ...changedSite, planId: concatProdId });
    return concatProdId;
  };

  const handlePlanChange = async (
    siteId: number,
    selectedProduct: FeedbackProduct | AnalyticsProduct,
  ): Promise<void> => {
    const concatProdId = setActiveProducts(selectedProduct);
    if (originalIdentifier.current !== concatProdId) {
      setIsChanged(true);
    } else {
      setIsChanged(false);
    }
    await onPlanChange(site.organizationId, siteId, { identifier: concatProdId });
  };

  useEffect(() => {
    const [analyticsProdSku, feedbackProdSku] = getProductSkusByPlanId(site.planId);
    setActiveAnalyticsProduct(getAnalyticsProductBySku(analyticsProdSku, productCatalog));
    setActiveFeedbackProduct(getFeedbackProductBySku(feedbackProdSku, productCatalog));
  }, [site, productCatalog]);

  useEffect(() => {
    setChangedSite(site);
    if (site.planId === originalIdentifier.current) {
      setIsChanged(false);
    }
  }, [site]);

  const planDowngradePeriod = isPlanDowngradePeriod(site);
  const legacyDiscount = priceInfo?.legacyDiscount;

  return (
    <StyledBox id={`site-box-${site.id}`} hasDivider={hasDivider}>
      <DetailsContainer>
        <SiteName>{site.name}</SiteName>
        <br />
        <MultiSkuAllowance
          account={account}
          site={changedSite}
          isTwoSkusActionRequired={site.isTwoSkusActionRequired}
        >
          <MultiSkuCollectionRates
            account={account}
            site={changedSite}
            onPlanChange={handlePlanChange}
            activeAnalyticsProductSku={activeAnalyticsProduct?.sku}
            activeFeedbackProductSku={activeFeedbackProduct?.sku}
            productCatalog={productCatalog}
          />
        </MultiSkuAllowance>
      </DetailsContainer>
      <CostContainer>
        <SitePriceMultiSku
          billingCycle={account.billingCycle}
          planDowngradePeriod={planDowngradePeriod}
          total={priceInfo.total}
          legacyDiscount={legacyDiscount}
          agencyDiscountPercentage={priceInfo.discountPercentage}
          highlighted={isChanged}
          bundleDiscount={priceInfo.bundleDiscount}
        />
      </CostContainer>
    </StyledBox>
  );
};
